import { ChevronRight, Home } from 'lucide-react'
import { Fragment } from 'react'
import { Link, useLocation, useParams, useMatches } from 'react-router'
import { cn } from '#app/utils/misc'

interface BreadcrumbItem {
  label: string
  href?: string
  icon?: React.ElementType
}

export function Breadcrumbs({ className }: { className?: string }) {
  const location = useLocation()
  const params = useParams()
  const matches = useMatches()
  
  // Generate breadcrumbs based on the current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', href: '/', icon: Home }
    ]

    let currentPath = ''
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`
      
      // Skip certain segments
      if (segment === 'settings' || segment === 'projects' || segment === 'admin') {
        breadcrumbs.push({
          label: formatSegmentLabel(segment),
          href: currentPath
        })
        return
      }
      
      // Handle project ID specially
      if (params.projectId && segment === params.projectId) {
        breadcrumbs.push({
          label: getProjectName(), // Dynamic project name
          href: `/projects/${params.projectId}`
        })
        return
      }
      
      // Handle dynamic segments (prefixed with $)
      if (segment.startsWith('$')) {
        return // Skip parameter placeholders
      }
      
      // Format segment labels
      const label = formatSegmentLabel(segment)
      
      // Don't add href for the last item (current page)
      const isLast = index === pathSegments.length - 1
      
      // Skip adding if it's a duplicate of the previous breadcrumb
      const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1]
      if (lastBreadcrumb && lastBreadcrumb.label === label) {
        return
      }
      
      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath
      })
    })
    
    return breadcrumbs
  }
  
  // Get project name (in a real app, this would come from data)
  const getProjectName = (): string => {
    // You could pass this via route data or context
    return 'Amsterdam Loft Renovation'
  }
  
  // Format segment labels for display
  const formatSegmentLabel = (segment: string): string => {
    // Special cases
    const specialCases: Record<string, string> = {
      // Main sections
      'settings': 'Settings',
      'projects': 'Projects',
      'dashboard': 'Dashboard',
      'admin': 'Admin',
      'users': 'Users',
      'resources': 'Resources',
      
      // Settings pages
      'profile': 'Profile',
      'two-factor': 'Two-Factor Auth',
      'passkeys': 'Passkeys',
      'connections': 'Connections',
      'change-email': 'Change Email',
      'password': 'Password',
      'photo': 'Photo',
      
      // Project stages
      'orientation': 'Orientation Phase',
      'planning': 'Planning Phase',
      'building': 'Building Phase',
      'finishing': 'Finishing Phase',
      
      // Project sections
      'budget': 'Budget',
      'timeline': 'Timeline',
      'documents': 'Documents',
      'team': 'Team',
      'tasks': 'Tasks',
      'moodboards': 'Mood Boards',
      'feed': 'Feed',
      'communication': 'Communication',
      'stories': 'Stories',
      'products': 'Products',
      'decisions': 'Decisions',
      
      // Admin
      'cache': 'Cache Management',
      'sqlite': 'SQLite',
      
      // Other
      'tos': 'Terms of Service',
      'privacy': 'Privacy Policy',
      'new': 'New',
      'edit': 'Edit',
      'delete': 'Delete',
    }
    
    if (specialCases[segment]) {
      return specialCases[segment]
    }
    
    // Default: capitalize and replace hyphens
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  
  const breadcrumbs = generateBreadcrumbs()
  
  // Don't show breadcrumbs on home page
  if (breadcrumbs.length <= 1) {
    return null
  }
  
  return (
    <nav 
      aria-label="Breadcrumb" 
      className={cn('flex items-center space-x-2 text-sm', className)}
    >
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1
        const Icon = item.icon
        
        return (
          <Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            
            {isLast ? (
              <span className="font-medium text-gray-900 flex items-center gap-1">
                {Icon && <Icon className="h-4 w-4" />}
                {item.label}
              </span>
            ) : item.href ? (
              <Link
                to={item.href}
                className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1"
              >
                {Icon && <Icon className="h-4 w-4" />}
                {item.label}
              </Link>
            ) : null}
          </Fragment>
        )
      })}
    </nav>
  )
}

// Mobile breadcrumbs - shows only current and parent
export function MobileBreadcrumbs({ className }: { className?: string }) {
  const location = useLocation()
  const params = useParams()
  
  const pathSegments = location.pathname.split('/').filter(Boolean)
  
  if (pathSegments.length === 0) {
    return null
  }
  
  const currentSegment = pathSegments[pathSegments.length - 1]
  const parentSegment = pathSegments[pathSegments.length - 2]
  
  const formatLabel = (segment: string): string => {
    const specialCases: Record<string, string> = {
      // Project stages (shortened for mobile)
      'orientation': 'Orientation',
      'planning': 'Planning',
      'building': 'Building',
      'finishing': 'Finishing',
      
      // Common sections (shortened)
      'dashboard': 'Dashboard',
      'projects': 'Projects',
      'settings': 'Settings',
      'profile': 'Profile',
      'budget': 'Budget',
      'timeline': 'Timeline',
      'documents': 'Docs',
      'team': 'Team',
      'tasks': 'Tasks',
      'moodboards': 'Moods',
      'feed': 'Feed',
      'stories': 'Stories',
      'products': 'Products',
    }
    
    return specialCases[segment] || 
      segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
  }
  
  const parentPath = '/' + pathSegments.slice(0, -1).join('/')
  
  return (
    <div className={cn('flex items-center gap-2 text-sm', className)}>
      {parentSegment && (
        <>
          <Link 
            to={parentPath}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            {formatLabel(parentSegment)}
          </Link>
          <ChevronRight className="h-4 w-4 text-gray-400" />
        </>
      )}
      <span className="font-medium text-gray-900">
        {currentSegment ? formatLabel(currentSegment) : ''}
      </span>
    </div>
  )
}